import { ArrowLeft, Calendar, Moon, TrendingUp } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useEffect } from "react";
import { get } from "@/services/axiosUitils";
import welcomeBg from "../../assets/homebg.png";
import { CHART_AXIS_TICK_LG, CHART_FONT_SIZES } from "../../utils/chartConfig";

// Parse sleep value - handles both number (minutes) and string formats like "7h 30 min", "0h 0 min"
// Numeric values from the API are in minutes (consistent with fitbit_scan/daily sleep_minutes field)
const parseSleepHours = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") {
    if (isNaN(value) || value === 0) return 0;
    // API returns minutes; convert to hours
    return value / 60;
  }
  const str = String(value).trim();
  if (!str || str === "0") return 0;
  // Format: "7h 30 min" or "7h 30min" or "7h"
  const hoursMinMatch = str.match(/(\d+)\s*h\s*(\d+)?\s*m?i?n?/i);
  if (hoursMinMatch) {
    const h = parseInt(hoursMinMatch[1]) || 0;
    const m = parseInt(hoursMinMatch[2]) || 0;
    return h + m / 60;
  }
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

const SleepDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sleepData, setSleepData] = useState<any[]>([]);

  // Get selected date from FitnessPage navigation
  const selectedDate = location.state?.selectedDate || null;
  const selectedDateObj = location.state?.selectedDateObj || null;

  const [summaryData, setSummaryData] = useState({
    stages: {
      deep: "0h 0 min",
      light: "0h 0 min",
      rem: "0h 0 min",
      wake: "0h 0 min",
    },
    totalHoursAsleep: "0h 0 min",
    sleep_hours: "0h 0 min",
    days: 7,
    weeks: 4,
  });
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  // Fetch sleep data from API
  const fetchSleepData = async (type: "weekly" | "monthly") => {
    try {
      setLoading(true);
      setError(null);

      // Build API URL with date parameter if available
      const dateParam = selectedDate ? `&date=${selectedDate}` : "";
      const apiUrl = `fitbit_scan/sleep-graph/?type=${type}${dateParam}`;

      const response = await get(apiUrl);

      // Handle both boolean and object success formats
      const isSuccess =
        typeof response.success === "boolean"
          ? response.success
          : response.success?.code === "Retrieved";

      if (isSuccess && response.summary) {
        setSummaryData(response.summary);
        setDateRange({
          start_date: response.start_date,
          end_date: response.end_date,
        });

        // Transform API data to match chart format
        if (type === "weekly") {
          const transformedData = response.graph.map((item: any) => {
            const hrs = parseSleepHours(item.value);
            return {
              day: item.day,
              date: item.date,
              hours: hrs,
              deep: hrs * 0.25,
              light: hrs * 0.6,
              rem: hrs * 0.15,
            };
          });
          setSleepData(transformedData);
        } else {
          const transformedData = response.graph.map((item: any) => ({
            week: item.week,
            hours: parseSleepHours(item.value),
          }));
          setSleepData(transformedData);
        }
      } else {
        setError("Failed to fetch sleep data");
        setSleepData([]);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch sleep data");
      setSleepData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab: "weekly" | "monthly") => {
    setActiveTab(tab);
    fetchSleepData(tab);
  };

  // Load initial data
  useEffect(() => {
    fetchSleepData("weekly");
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      return "";
    }
    const startDate = new Date(dateRange.start_date).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
      }
    );
    const endDate = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${startDate} - ${endDate}`;
  };

  // Get current night's sleep data - return original string format from API
  const getCurrentSleepData = () => {
    const hours = summaryData.totalHoursAsleep || summaryData.sleep_hours || "0h 0 min";
    const deep = summaryData.stages?.deep || "0h 0 min";
    const light = summaryData.stages?.light || "0h 0 min";
    const rem = summaryData.stages?.rem || "0h 0 min";
    const wake = summaryData.stages?.wake || "0h 0 min";

    return { hours, deep, light, rem, wake };
  };

  const currentSleep = getCurrentSleepData();

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="z-50 rounded-xl border-2 border-gray-300 bg-white p-3 shadow-2xl dark:border-gray-600 dark:bg-gray-800">
          <p className="mb-2 font-bold text-gray-900 dark:text-white">
            {data.date ? data.date : label}
          </p>
          <div className="space-y-1">
            <p className="text-callout font-semibold text-blue-600 dark:text-blue-400">
              Total: {data.hours?.toFixed(1)} hrs
            </p>
            {/* Show stages breakdown if available */}
            {(data.deep > 0 || data.light > 0 || data.rem > 0) && (
              <div className="mt-2 space-y-1 border-t border-gray-200 pt-2 text-caption1 dark:border-gray-600">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600 dark:text-gray-400">Deep:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{data.deep?.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600 dark:text-gray-400">Light:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{data.light?.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600 dark:text-gray-400">REM:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{data.rem?.toFixed(1)}h</span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-title3 font-bold text-foreground">Sleep</h1>
            <p className="text-caption1 text-muted-foreground">
              {selectedDate ? selectedDate : "Your rest and recovery"}
            </p>
          </div>
          <div className="w-10" />
        </div>

        <Card className="rounded-2xl border border-border bg-gradient-to-br from-blue-50 to-blue-100 p-5 shadow-lg dark:from-blue-950 dark:to-blue-900">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-500/20">
              <Moon className="h-7 w-7 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="text-large-title font-bold text-foreground">
                {loading
                  ? "..."
                  : currentSleep.hours}
              </div>
              <p className="text-footnote text-muted-foreground">last night</p>
              {formatDateRange() && (
                <p className="text-caption1 text-gray-500">{formatDateRange()}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 border-t border-blue-200/50 pt-3">
            <div className="text-center">
              <div className="text-title3 font-bold text-blue-600">
                {loading
                  ? "..."
                  : currentSleep.deep}
              </div>
              <div className="text-caption1 text-muted-foreground">Deep</div>
            </div>
            <div className="text-center">
              <div className="text-title3 font-bold text-yellow-600">
                {loading
                  ? "..."
                  : currentSleep.light}
              </div>
              <div className="text-caption1 text-muted-foreground">Light</div>
            </div>
            <div className="text-center">
              <div className="text-title3 font-bold text-orange-600">
                {loading
                  ? "..."
                  : currentSleep.rem}
              </div>
              <div className="text-caption1 text-muted-foreground">REM</div>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-blue-50/30 p-4 shadow-lg dark:bg-blue-950/30">
          <div className="flex gap-3">
            <TrendingUp className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <div>
              <h4 className="mb-1 text-headline font-semibold text-foreground">
                About Sleep Tracking
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Sleep stages are tracked using heart rate variability and
                movement patterns. Deep sleep is crucial for physical recovery,
                while REM sleep supports memory and learning. Adults need 7-9
                hours nightly.
              </p>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-card shadow-lg">
          <div className="p-4">
            <div className="inline-flex w-full max-w-sm rounded-xl bg-muted p-1">
              <button
                onClick={() => handleTabChange("weekly")}
                disabled={loading}
                className={`flex-1 rounded-lg px-4 py-2.5 text-subhead font-medium transition-all duration-200 disabled:opacity-50 ${activeTab === "weekly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-background/50"
                  }`}
              >
                Weekly
              </button>
              <button
                onClick={() => handleTabChange("monthly")}
                disabled={loading}
                className={`flex-1 rounded-lg px-4 py-2.5 text-subhead font-medium transition-all duration-200 disabled:opacity-50 ${activeTab === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-background/50"
                  }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="space-y-4 px-4 pb-4 pt-0">
            {activeTab === "weekly" && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">
                    This Week's Sleep ({sleepData.length}{" "}
                    {sleepData.length === 1 ? "day" : "days"})                    {dateRange.start_date && (
                      <span className="ml-2 text-footnote font-normal text-muted-foreground">
                        ({formatDateRange()})
                      </span>
                    )}                  </h3>
                  {loading && (
                    <div className="text-footnote text-primary">Loading...</div>
                  )}
                </div>

                {error ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <div className="mb-2 text-red-500">⚠️</div>
                    <div className="text-footnote text-red-600">{error}</div>
                    <button
                      onClick={() => fetchSleepData("weekly")}
                      className="mt-2 text-caption1 text-blue-600 hover:underline"
                    >
                      Try Again
                    </button>
                  </div>
                ) : sleepData.length === 0 && !loading ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <Moon className="mb-2 h-12 w-12 text-gray-300" />
                    <div className="text-footnote text-gray-500">
                      No sleep data available
                    </div>
                  </div>
                ) : sleepData.every(item => item.hours === 0) ? (
                  <div className="flex flex-col gap-2">
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={sleepData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            opacity={0.2}
                          />
                          <XAxis
                            dataKey="day"
                            stroke="hsl(var(--muted-foreground))"
                            tick={CHART_AXIS_TICK_LG}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            stroke="hsl(var(--muted-foreground))"                            domain={[0, 12]}
                            ticks={[0, 4, 8, 12]}
                            tickFormatter={(v) => `${v}h`}
                            tick={CHART_AXIS_TICK_LG}
                            tickLine={false}
                            axisLine={false}
                          />
                          <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="hours"
                            stroke="hsl(var(--muted))"
                            strokeWidth={2}
                            dot={{ r: 0 }}
                            activeDot={{ r: 4 }}
                            strokeDasharray="5 5"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-caption1 text-muted-foreground">
                        <span className="inline-block h-0.5 w-3 bg-gray-300" />
                        No sleep data recorded this week
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sleepData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          opacity={0.2}
                        />
                        <XAxis
                          dataKey="day"
                          stroke="hsl(var(--muted-foreground))"
                          tick={CHART_AXIS_TICK_LG}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"                          domain={[0, Math.max(...sleepData.map(d => d.hours), 1) < 12 ? 12 : Math.ceil(Math.max(...sleepData.map(d => d.hours)))]}
                          ticks={[0, 4, 8, 12]}
                          tickFormatter={(v) => `${v}h`}
                          tick={CHART_AXIS_TICK_LG}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="hours"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={{ fill: "#3b82f6", r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {activeTab === "monthly" && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">
                    This Month's Sleep ({sleepData.length}{" "}
                    {sleepData.length === 1 ? "week" : "weeks"})
                    {dateRange.start_date && (
                      <span className="ml-2 text-footnote font-normal text-muted-foreground">
                        ({formatDateRange()})
                      </span>
                    )}
                  </h3>
                  {loading && (
                    <div className="text-footnote text-primary">Loading...</div>
                  )}
                </div>

                {error ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <div className="mb-2 text-red-500">⚠️</div>
                    <div className="text-footnote text-red-600">{error}</div>
                    <button
                      onClick={() => fetchSleepData("monthly")}
                      className="mt-2 text-caption1 text-blue-600 hover:underline"
                    >
                      Try Again
                    </button>
                  </div>
                ) : sleepData.length === 0 && !loading ? (
                  <div className="flex h-64 flex-col items-center justify-center text-center">
                    <Moon className="mb-2 h-12 w-12 text-gray-300" />
                    <div className="text-footnote text-muted-foreground">
                      No sleep data available
                    </div>
                  </div>
                ) : sleepData.every(item => item.hours === 0) ? (
                  <div className="flex flex-col gap-2">
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={sleepData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            opacity={0.2}
                          />
                          <XAxis
                            dataKey="week"
                            stroke="hsl(var(--muted-foreground))"
                            tick={CHART_AXIS_TICK_LG}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            stroke="hsl(var(--muted-foreground))"                            domain={[0, 12]}
                            ticks={[0, 4, 8, 12]}
                            tickFormatter={(v) => `${v}h`}
                            tick={CHART_AXIS_TICK_LG}
                            tickLine={false}
                            axisLine={false}
                          />
                          <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="hours"
                            stroke="hsl(var(--muted))"
                            strokeWidth={2}
                            dot={{ r: 0 }}
                            activeDot={{ r: 4 }}
                            strokeDasharray="5 5"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-caption1 text-muted-foreground">
                        <span className="inline-block h-0.5 w-3 bg-gray-300" />
                        No sleep data recorded this month
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sleepData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          opacity={0.2}
                        />
                        <XAxis
                          dataKey="week"
                          stroke="hsl(var(--muted-foreground))"
                          tick={CHART_AXIS_TICK_LG}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"                          domain={[0, Math.max(...sleepData.map(d => d.hours), 1) < 12 ? 12 : Math.ceil(Math.max(...sleepData.map(d => d.hours)))]}
                          ticks={[0, 4, 8, 12]}
                          tickFormatter={(v) => `${v}h`}
                          tick={CHART_AXIS_TICK_LG}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ stroke: "hsl(var(--muted))", strokeWidth: 1 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="hours"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={{ fill: "#3b82f6", r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SleepDetail;
